/* Registering a framework: what the form holds, when it may be submitted, and the presets.
 *
 * **A framework is a code at a version, and the version is part of its identity.** Registering
 * `2024.2` beside `2024.1` gives a tenant two frameworks; a pack always names which one it evidenced,
 * and the backend refuses a *different* definition under an existing `(code, version)` with a 409
 * because packs may already embed it. So the duplicate check here is not politeness — it stops
 * somebody producing two frameworks a pack could not tell apart.
 *
 * **Every rule below mirrors one the server already enforces**, rather than inventing a stricter
 * client. `Control.__post_init__` refuses a control with no evidence kind — *"a control nothing can
 * substantiate would read as satisfied in every pack"* — `Framework.__post_init__` refuses an empty
 * control list and duplicate control codes, and both fields of a control must be non-blank. Checking
 * them here means a reader is told before they submit; the server is still the authority.
 */

import { EVIDENCE_KINDS } from '@/shared/api/generated/compliance';
import type { EvidenceKind, Framework } from '@/shared/api/generated/compliance';

export interface ControlDraft {
  readonly code: string;
  readonly title: string;
  readonly evidence: readonly EvidenceKind[];
}

export interface FrameworkDraft {
  readonly code: string;
  readonly version: string;
  readonly title: string;
  readonly description: string;
  readonly controls: readonly ControlDraft[];
}

export const BLANK_CONTROL: ControlDraft = { code: '', title: '', evidence: [] };

/** One empty control rather than none.
 *
 * A framework with no controls generates an empty pack that reads as a clean audit, which the domain
 * refuses outright — so the form starts with the minimum a valid framework has, and the first control
 * is not removable. An empty list would present "add a control" as optional.
 */
export const EMPTY_FRAMEWORK_DRAFT: FrameworkDraft = {
  code: '',
  version: '',
  title: '',
  description: '',
  controls: [BLANK_CONTROL],
};

/** Toggle one evidence kind on a control, keeping the contract's published order.
 *
 * Ordered by `EVIDENCE_KINDS` rather than by the order somebody clicked, so what is sent matches what
 * the detail screen will render and two controls naming the same kinds are byte-identical.
 */
export function toggleEvidence(control: ControlDraft, kind: EvidenceKind): ControlDraft {
  const has = control.evidence.includes(kind);
  const next = has
    ? control.evidence.filter((each) => each !== kind)
    : [...control.evidence, kind];
  return { ...control, evidence: EVIDENCE_KINDS.filter((each) => next.includes(each)) };
}

export function controlIsValid(control: ControlDraft): boolean {
  return (
    control.code.trim() !== '' &&
    control.title.trim() !== '' &&
    control.evidence.length > 0
  );
}

/** Whether this `(code, version)` is already registered.
 *
 * Trimmed on both sides and compared exactly — not case-folded. A framework code is a published
 * identifier, and the backend's unique index is on the stored value, so folding here would report a
 * clash the server would accept and hide one it would refuse.
 */
export function isDuplicate(
  draft: FrameworkDraft,
  existing: readonly Framework[],
): boolean {
  const code = draft.code.trim();
  const version = draft.version.trim();
  if (code === '' || version === '') return false;
  return existing.some(
    (framework) => framework.code === code && framework.version === version,
  );
}

/** Control codes that appear more than once, in the order they first repeat.
 *
 * `Framework.__post_init__` refuses these, and the reference's own form does not check them — so this
 * is a real refusal the screen would otherwise discover as a 422 after somebody had typed out five
 * controls. Returned as the offending codes rather than a boolean because the message names them.
 */
export function duplicateControlCodes(draft: FrameworkDraft): readonly string[] {
  const seen = new Set<string>();
  const repeated = new Set<string>();
  for (const control of draft.controls) {
    const code = control.code.trim();
    if (code === '') continue;
    if (seen.has(code)) repeated.add(code);
    seen.add(code);
  }
  return [...repeated];
}

export function frameworkDraftIsValid(
  draft: FrameworkDraft,
  existing: readonly Framework[],
): boolean {
  return (
    draft.code.trim() !== '' &&
    draft.version.trim() !== '' &&
    draft.title.trim() !== '' &&
    draft.description.trim() !== '' &&
    draft.controls.every(controlIsValid) &&
    !isDuplicate(draft, existing) &&
    duplicateControlCodes(draft).length === 0
  );
}

/** What the hint under the form says: the first blocking reason, or what registering will do.
 *
 * Ordered so the reason names the field a reader should look at next, top to bottom through the form.
 * The last sentence is the one that matters: registering evidences nothing on its own, and existing
 * packs are untouched because each names the framework version it evidenced.
 */
export function frameworkHintKey(
  draft: FrameworkDraft,
  existing: readonly Framework[],
): string {
  if (draft.code.trim() === '') return 'compliance.reg.needCode';
  if (draft.version.trim() === '') return 'compliance.reg.needVersion';
  if (isDuplicate(draft, existing)) return 'compliance.reg.duplicate';
  if (draft.title.trim() === '') return 'compliance.reg.needTitle';
  if (draft.description.trim() === '') return 'compliance.reg.needDescription';
  if (duplicateControlCodes(draft).length > 0) return 'compliance.reg.duplicateControl';
  if (!draft.controls.every(controlIsValid)) return 'compliance.reg.needControls';
  return 'compliance.reg.willRegister';
}

/* ---------------------------------------------------------------- presets

 * Four standards, as starting points.
 *
 * **A preset fills the form and certifies nothing**, which the screen says in those words. The
 * control codes are the standards' own citations — `S.8(4)`, `Art.30`, `A.5.12` — and the evidence
 * mapping is this platform's judgement about which of its six collectors substantiates each one. Both
 * stay editable, because a customer's auditor may well disagree about the mapping and the form must
 * not pretend otherwise.
 *
 * Structural here, prose in the catalogue: a citation is not translatable and a requirement is. The
 * `titleKey` on each control resolves to the sentence a reader reads.
 */
export interface PresetControl {
  readonly code: string;
  readonly titleKey: string;
  readonly evidence: readonly EvidenceKind[];
}

export interface FrameworkPreset {
  readonly code: string;
  readonly version: string;
  readonly titleKey: string;
  readonly descriptionKey: string;
  /** Where the standard applies — "India", "EU", "International", "Cards". */
  readonly regionKey: string;
  readonly controls: readonly PresetControl[];
}

export const FRAMEWORK_PRESETS: readonly FrameworkPreset[] = [
  {
    code: 'DPDP',
    version: '2023.act',
    titleKey: 'compliance.preset.dpdp.title',
    descriptionKey: 'compliance.preset.dpdp.description',
    regionKey: 'compliance.preset.region.india',
    controls: [
      {
        code: 'S.8(4)',
        titleKey: 'compliance.preset.dpdp.c1',
        evidence: ['pii_classification'],
      },
      {
        code: 'S.8(5)',
        titleKey: 'compliance.preset.dpdp.c2',
        evidence: ['audit_chain_integrity', 'change_approvals'],
      },
      {
        code: 'S.8(6)',
        titleKey: 'compliance.preset.dpdp.c3',
        evidence: ['incident_response'],
      },
      {
        code: 'S.8(3)',
        titleKey: 'compliance.preset.dpdp.c4',
        evidence: ['remediation_governance', 'quality_scoring'],
      },
    ],
  },
  {
    code: 'GDPR',
    version: '2016.679',
    titleKey: 'compliance.preset.gdpr.title',
    descriptionKey: 'compliance.preset.gdpr.description',
    regionKey: 'compliance.preset.region.eu',
    controls: [
      {
        code: 'Art.30',
        titleKey: 'compliance.preset.gdpr.c1',
        evidence: ['audit_chain_integrity', 'pii_classification'],
      },
      {
        code: 'Art.32',
        titleKey: 'compliance.preset.gdpr.c2',
        evidence: ['audit_chain_integrity', 'change_approvals'],
      },
      {
        code: 'Art.33',
        titleKey: 'compliance.preset.gdpr.c3',
        evidence: ['incident_response'],
      },
      {
        code: 'Art.5(1)(d)',
        titleKey: 'compliance.preset.gdpr.c4',
        evidence: ['remediation_governance', 'quality_scoring'],
      },
      {
        code: 'Art.25',
        titleKey: 'compliance.preset.gdpr.c5',
        evidence: ['change_approvals', 'pii_classification'],
      },
    ],
  },
  {
    code: 'ISO-27001',
    version: '2022',
    titleKey: 'compliance.preset.iso.title',
    descriptionKey: 'compliance.preset.iso.description',
    regionKey: 'compliance.preset.region.international',
    controls: [
      {
        code: 'A.5.12',
        titleKey: 'compliance.preset.iso.c1',
        evidence: ['pii_classification'],
      },
      {
        code: 'A.8.15',
        titleKey: 'compliance.preset.iso.c2',
        evidence: ['audit_chain_integrity'],
      },
      {
        code: 'A.8.32',
        titleKey: 'compliance.preset.iso.c3',
        evidence: ['change_approvals', 'remediation_governance'],
      },
      {
        code: 'A.5.26',
        titleKey: 'compliance.preset.iso.c4',
        evidence: ['incident_response'],
      },
    ],
  },
  {
    code: 'PCI-DSS',
    version: '4.0',
    titleKey: 'compliance.preset.pci.title',
    descriptionKey: 'compliance.preset.pci.description',
    regionKey: 'compliance.preset.region.cards',
    controls: [
      {
        code: 'Req.3',
        titleKey: 'compliance.preset.pci.c1',
        evidence: ['pii_classification'],
      },
      {
        code: 'Req.10',
        titleKey: 'compliance.preset.pci.c2',
        evidence: ['audit_chain_integrity'],
      },
      {
        code: 'Req.12.10',
        titleKey: 'compliance.preset.pci.c3',
        evidence: ['incident_response'],
      },
    ],
  },
];

/** Whether a preset's `(code, version)` is already registered — so it is offered as taken. */
export function presetIsTaken(
  preset: FrameworkPreset,
  existing: readonly Framework[],
): boolean {
  return existing.some(
    (framework) => framework.code === preset.code && framework.version === preset.version,
  );
}

/** Whether the draft currently *is* this preset's identity, so its card reads as chosen. */
export function presetIsChosen(preset: FrameworkPreset, draft: FrameworkDraft): boolean {
  return draft.code === preset.code && draft.version === preset.version;
}
